import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
  faAdd,
  faTimes,
  faCheck,
} from "@fortawesome/pro-regular-svg-icons";
import ConnectorStatusBadge from "../connector/ConnectorStatusBadge";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { deleteChargingPoint } from "../../api/ChargingPoint";
import {
    deleteChargingConnector,
 } from "../../api/ChargingConnector";

const SiteHierarchy = ({ chargingSite, resyncChargingSite = () => {} }) => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const handleDeletePoint = async (chargingPointPublicId) => {
    const token = await getAccessToken();
    try {
      await deleteChargingPoint(chargingPointPublicId, token);
      toast.success(t("toast.deleted", { entity: t("entity.chargingPoint") }));
      resyncChargingSite();
    } catch (error) {
      console.error("Error deleting charging point:", error);
      toast.error(t("toast.failedDelete", { entity: t("entity.chargingPoint") }));
    }
  };

  const handleDeleteConnector = async (chargingConnectorPublicId) => {
    const token = await getAccessToken();
    try {
      await deleteChargingConnector(chargingConnectorPublicId, token);
      toast.success(t("toast.deleted", { entity: t("entity.connector") }));
      resyncChargingSite();
    } catch (error) {
      console.error("Error deleting charging connector:", error);
      toast.error(t("toast.failedDelete", { entity: t("entity.connector") }));
    }
  };

  return (
    <>
      {/* Tab content */}
      <div className="overflow-x-auto pb-[250px] mb-[-250px]">
        <table className="table-default w-full">
          <thead>
            <tr>
              <th className="text-left cursor-pointer">{t("entity.point")} - {t("entity.connector")}</th>
              <th className="text-left cursor-pointer">{t("common.online")}</th>
              <th className="text-left cursor-pointer">{t("common.type")}</th>
              <th className="text-left cursor-pointer">{t("common.status")}</th>
              <th>
                <div className="flex w-full justify-end items-center">
                  <button
                    className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                    onClick={() => {
                      navigate("/point/create", {
                        state: {
                          chargingSitePublicId:
                            chargingSite.chargingSitePublicId,
                        },
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {t("pages.addNewPoint")}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {chargingSite?.chargingPoints?.map((point) => (
              <React.Fragment key={point.chargingPointPublicId}>
                <tr
                  key={point.chargingPointPublicId}
                  className="hover:bg-mute-100"
                >
                  <td className="text-left">
                    <div className="flex items-center gap-2 flex-row">
                      {point.chargingPointName}
                      <span className="text-sm font-medium text-mute-800">
                        {point.name}
                      </span>
                    </div>
                  </td>
                  <td>{enabledBadge(point.isOnline)}</td>
                  <td>{t("entity.point")}</td>
                  <td></td>

                  <td className="text-right">
                    <CustomDropdown
                      isRight={true}
                      ContentComponent={() => (
                        <div className="w-[13rem] flex flex-col items-start bg-white p-2 rounded-lg">
                          <button
                            className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                            onClick={() => {
                              navigate(`/connector/create`, {
                                state: {
                                  chargingSitePublicId:
                                    chargingSite.chargingSitePublicId,
                                  chargingPointPublicId:
                                    point.chargingPointPublicId,
                                  operatorOrganizationPublicId:
                                    chargingSite?.operatorBusinessPublicId ?? null,
                                },
                              });
                            }}
                          >
                            <FontAwesomeIcon icon={faAdd} /> {t("pages.addConnector")}
                          </button>
                          <button
                            className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                            onClick={() => {
                              navigate(
                                `/point/${point.chargingPointPublicId}`,
                                {
                                  state: {
                                    chargingSitePublicId:
                                      chargingSite.chargingSitePublicId,
                                  },
                                }
                              );
                            }}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                          </button>
                          <button
                            className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                            onClick={() => {
                              setConfirmState({
                                isOpen: true,
                                title: t("confirm.deleteEntity", { entity: t("entity.chargingPoint"), name: point.chargingPointName }),
                                confirm: () => {
                                  handleDeletePoint(
                                    point.chargingPointPublicId
                                  );
                                },
                              });
                            }}
                          >
                            <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                          </button>
                        </div>
                      )}
                    >
                      <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                        <FontAwesomeIcon icon={faEllipsis} />
                      </div>
                    </CustomDropdown>
                  </td>
                </tr>

                {point?.chargingConnectors?.map((connector) => {
                        return (
                          <tr
                            key={connector.chargingConnectorPublicId}
                            className="hover:bg-mute-100"
                          >
                            <td className="text-left pl-8">
                              <div className="flex items-center gap-2 pl-8">
                                <span className="text-sm font-medium text-mute-800">
                                  {connector.chargingConnectorName}
                                </span>
                              </div>
                            </td>
                            <td></td>
                            <td>{t("entity.connector")}</td>
                            <td>
                                    <ConnectorStatusBadge status={connector.chargingConnectorStatus} />
                            </td>

                            <td className="text-right">
                              <CustomDropdown
                                isRight={true}
                                ContentComponent={() => (
                                  <div className="w-[13rem] flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => {
                                        navigate(
                                          `/connector/${connector.chargingConnectorPublicId}`,
                                          {
                                            state: {
                                              chargingPointPublicId:
                                                point.chargingPointPublicId,
                                              chargingSitePublicId:
                                                chargingSite.chargingSitePublicId,
                                              operatorOrganizationPublicId:
                                                chargingSite?.operatorBusinessPublicId ?? null,
                                            },
                                          }
                                        );
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} />{" "}
                                      {t("common.edit")}
                                    </button>
                                    
                                    <button
                                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => {
                                        setConfirmState({
                                          isOpen: true,
                                          title: t("confirm.deleteEntity", { entity: t("entity.connector"), name: connector.name }),
                                          confirm: () => {
                                            handleDeleteConnector(
                                              connector.chargingConnectorPublicId
                                            );
                                          },
                                        });
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faCircleMinus} />{" "}
                                      {t("common.delete")}
                                    </button>
                                  </div>
                                )}
                              >
                                <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                  <FontAwesomeIcon icon={faEllipsis} />
                                </div>
                              </CustomDropdown>
                            </td>
                          </tr>
                        );
                      
                })}
              </React.Fragment>
            ))}
            {chargingSite?.chargingPoints?.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-4">
                  {t("site.noPointsFound")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default SiteHierarchy;


const enabledBadge = (isEnabled) => {
  const statusBGColor = {
    true: "text-secondary-500",
    false: "text-error-500",
  };

  return (
    <div className="flex items-center">
      <FontAwesomeIcon
        icon={isEnabled ? faCheck : faTimes}
        className={`text-xl font-bold ${statusBGColor[isEnabled]}`}
      />
    </div>
  );
};
